import type { ColumnDef } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/components/ui/cn";
import { DropdownIcon } from "~/components/ui/icons/dropdown";
import { ExternalLinkDefaultIcon } from "~/components/ui/icons/external-link-default";
import { brandState } from "~/stores/brand";
import { activeSaleIdState, shortActiveSaleIdState } from "~/stores/sales";
import { MEUSNIDUS_BASE_URL } from "~/utils/url";

import type { BillingErrorData } from "./billing-error-table";
import { BillingErrorTable } from "./billing-error-table";
import { useBilling } from "./use-billing";
import { useBillingState } from "./use-billing-state";

export const BillingErrorList: React.FC = () => {
  const activeSaleId = useAtomValue(activeSaleIdState);
  const brand = useAtomValue(brandState);
  const { t } = useTranslation();

  const { data: billingState } = useBillingState();
  const { data: billing } = useBilling();

  const shortActiveSaleId = useAtomValue(shortActiveSaleIdState);

  const columns: ColumnDef<BillingErrorData>[] = [
    {
      accessorKey: "errors",
      header: () => <>{t("billing.errors.errors")}</>,
      cell: ({ row }) => {
        return (
          <button
            type="button"
            {...{
              onClick: row.getToggleExpandedHandler(),
              style: { cursor: "pointer" },
            }}
            className="flex items-center space-x-2"
          >
            <DropdownIcon
              className={cn("size-6 shrink-0 text-gray-900", {
                "-rotate-90": !row.getIsExpanded(),
              })}
            />

            <span
              className={cn("text-sm font-semibold lowercase text-red-600", {
                "text-orange-600": row.original.type === "warning",
              })}
            >
              {row.original.code}
            </span>
            <span
              className={cn(
                "rounded bg-red-350 px-1 py-0.5 text-xs font-semibold text-red-600",
                {
                  "bg-orange-300 text-orange-600":
                    row.original.type === "warning",
                },
              )}
            >
              {row.original.errors.length}
            </span>
          </button>
        );
      },
    },
    {
      accessorKey: "details",
      header: () => <>{t("billing.errors.details")}</>,
      cell: ({ getValue }) => (
        <div>
          <div className="flex text-xs font-semibold uppercase tracking-wider text-gray-700 sm:hidden">
            {t("billing.errors.details")}
          </div>
          <div>{getValue<string>()}</div>
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: () => <>{t("billing.errors.actions")}</>,
      cell: ({ row, getValue }) => (
        <div>
          <div className="flex text-xs font-semibold uppercase tracking-wider text-gray-700 sm:hidden">
            {t("billing.errors.actions")}
          </div>
          {row.original.url ? (
            <a
              href={row.original.url}
              target="_blank"
              className="flex items-center space-x-1 text-xs font-semibold text-green-900"
              rel="noreferrer"
            >
              <ExternalLinkDefaultIcon className="size-4 shrink-0" />
              <span>{getValue<string>()}</span>
            </a>
          ) : (
            <span className="text-xs font-semibold text-gray-700">
              {getValue<string>()}
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "whoCanResolve",
      header: () => <>{t("billing.errors.who_can_resolve")}</>,
      cell: ({ getValue }) => (
        <div>
          <div className="flex text-xs font-semibold uppercase tracking-wider text-gray-700 sm:hidden">
            {t("billing.errors.who_can_resolve")}
          </div>
          <div>{getValue<string>()}</div>
        </div>
      ),
    },
  ];

  const data: BillingErrorData[] = useMemo(() => {
    const data: BillingErrorData[] = [];

    if (
      (billing?.errors.filter((error) => error.code === "no_variant_id")
        .length ?? 0) > 0
    )
      data.push({
        url: null,
        actions: t("billing.errors.no_variant_id.actions"),
        code: "no_variant_id",
        details: t("billing.errors.no_variant_id.details"),
        errors:
          billing?.errors.filter((error) => error.code === "no_variant_id") ??
          [],
        type: "error",
        whoCanResolve: t("billing.errors.no_variant_id.who_can_resolve"),
      });
    if (
      (billing?.errors.filter(
        (error) => error.code === "quantity_not_consistent",
      ).length ?? 0) > 0
    )
      data.push({
        url: null,
        actions: t("billing.errors.quantity_not_consistent.actions"),
        code: "quantity_not_consistent",
        details: t("billing.errors.quantity_not_consistent.details"),
        errors:
          billing?.errors.filter(
            (error) => error.code === "quantity_not_consistent",
          ) ?? [],
        type: "error",
        whoCanResolve: t(
          "billing.errors.quantity_not_consistent.who_can_resolve",
        ),
      });
    if (
      (billing?.errors.filter(
        (error) => error.code === "buying_price_not_correct",
      ).length ?? 0) > 0
    )
      data.push({
        url: `${MEUSNIDUS_BASE_URL}/vente/${activeSaleId ?? ""}`,
        actions: t("billing.errors.buying_price_not_correct.actions"),
        code: "buying_price_not_correct",
        details: t("billing.errors.buying_price_not_correct.details"),
        errors:
          billing?.errors.filter(
            (error) => error.code === "buying_price_not_correct",
          ) ?? [],
        type: "error",
        whoCanResolve: t(
          "billing.errors.buying_price_not_correct.who_can_resolve",
        ),
      });
    if (
      (billing?.errors.filter(
        (error) => error.code === "missing_buying_price_info",
      ).length ?? 0) > 0
    )
      data.push({
        url: `${MEUSNIDUS_BASE_URL}/vente/${activeSaleId ?? ""}`,
        actions: t("billing.errors.missing_buying_price_info.actions"),
        code: "missing_buying_price_info",
        details: t("billing.errors.missing_buying_price_info.details"),
        errors:
          billing?.errors.filter(
            (error) => error.code === "missing_buying_price_info",
          ) ?? [],
        type: "error",
        whoCanResolve: t(
          "billing.errors.missing_buying_price_info.who_can_resolve",
        ),
      });
    if ((billingState?.pendingClaimClosingOrderIds.length ?? 0) > 0)
      data.push({
        url: `${brand?.id ? "/" + brand.id : ""}/orders?sid=${shortActiveSaleId}&filter=filters&offset=0&claimFilter=PENDING&sort=CREATED_DESC`,
        actions: t("billing.errors.claim_unfulfilled.actions"),
        code: "claim_unfulfilled",
        details: t("billing.errors.claim_unfulfilled.details"),
        errors:
          billingState?.pendingClaimClosingOrderIds.map((orderId) => ({
            __typename: "BillingStateError",
            id: orderId,
          })) ?? [],
        type: "error",
        whoCanResolve: t("billing.errors.claim_unfulfilled.who_can_resolve"),
      });

    if ((billingState?.pendingOrderDeliveryOrderIds.length ?? 0) > 0)
      data.push({
        url: `${brand?.id ? "/" + brand.id : ""}/orders?sid=${shortActiveSaleId}&filter=filters&offset=0&shippingFilter=IN_PROGRESS&sort=CREATED_DESC`,
        actions: t("billing.errors.order_not_delivered.actions"),
        code: "order_not_delivered",
        details: t("billing.errors.order_not_delivered.details"),
        errors:
          billingState?.pendingOrderDeliveryOrderIds.map((orderId) => ({
            __typename: "BillingStateError",
            id: orderId,
          })) ?? [],
        type: "warning",
        whoCanResolve: t("billing.errors.order_not_delivered.who_can_resolve"),
      });

    if ((billingState?.pendingReturnValidationOrderIds.length ?? 0) > 0)
      data.push({
        url: `${brand?.id ? "/" + brand.id : ""}?sid=${shortActiveSaleId}&filter=filters&offset=0&returnFilter=PENDING&sort=CREATED_DESC`,
        actions: t("billing.errors.return_not_validated.actions"),
        code: "return_not_validated",
        details: t("billing.errors.return_not_validated.details"),
        errors:
          billingState?.pendingReturnValidationOrderIds.map((orderId) => ({
            __typename: "BillingStateError",
            id: orderId,
          })) ?? [],
        type: "warning",
        whoCanResolve: t("billing.errors.return_not_validated.who_can_resolve"),
      });

    return data;
  }, [activeSaleId, brand?.id, billing, billingState, shortActiveSaleId, t]);

  if (data.length === 0) return null;

  return <BillingErrorTable columns={columns} data={data} />;
};
